import { nanoid } from 'nanoid';

import { type ExtendToCellEvent } from '../events/types';
import { type SelectionRange, type SelectionState } from '../state/types';

import { type ReducerResponse } from './types';

/**
 * Extends the active cell range to the requested lead position. A non-cell mode
 * is normalized to a new cell range anchored at current focus, preserving a
 * predictable Shift+click recovery path.
 */
export function extendToCellReducer(
  state: SelectionState,
  event: ExtendToCellEvent
): ReducerResponse {
  // ---------------------------------------------------------------------------
  // 1. HANDLE MODE MISMATCH (Graceful Recovery)
  // ---------------------------------------------------------------------------
  // If we are in Row/Col mode and the user Shift+Clicks a cell, we
  // switch to Cell mode. We use the currently focused cell as the Anchor.

  if (state.mode !== 'cell') {
    // Fallback: If for some reason we have no focus, treat it as a fresh click.
    const anchor = state.focusedCell || event.cell;
    const newSelection: SelectionRange = {
      id: nanoid(),
      anchor: anchor, // Pivot around the active focus
      lead: event.cell
    };

    return {
      delta: {
        kind: 'selectionReplaced', // Must be a Replace because mode changed
        source: event.source,
        selections: [newSelection],
        // We explicitly keep the focus where it was (or set it to the new anchor)
        // so we don't send a focusChange delta that hides it.
        focusChange: state.focusedCell ? undefined : { prev: null, next: anchor }
      },
      next: {
        ...state,
        mode: 'cell', // Force the switch
        focusedCell: anchor, // Ensure focus is set
        selections: [newSelection],
        lastInteractionSource: event.source
      }
    };
  }

  // ---------------------------------------------------------------------------
  // 2. HANDLE MULTI-SELECTION EDGE CASE
  // ---------------------------------------------------------------------------
  // If the user has multiple separate cells selected, and they Shift+Click,
  // standard behavior (Excel/Sheets) is to extend the *most recently created* range.
  // We don't throw; we just grab the last one.

  const activeSelectionIndex = state.selections.length - 1;
  const activeSelection = state.selections[activeSelectionIndex];

  // Safety check: Should be impossible in a valid state, but returns noop just in case.
  if (!activeSelection) {
    return { next: state, delta: { kind: 'noop' } };
  }

  // ---------------------------------------------------------------------------
  // 3. OPTIMIZATION: NO-OP CHECK (only during drag)
  // ---------------------------------------------------------------------------
  // Skip redundant updates when dragging within the same cell.
  // On pointer up (isDragging = false), we always emit selectionModified to finalize.

  if (
    event.isDragging &&
    activeSelection.lead.row.index === event.cell.row.index &&
    activeSelection.lead.col.index === event.cell.col.index
  ) {
    return { next: state, delta: { kind: 'noop' } };
  }

  // ---------------------------------------------------------------------------
  // 4. STANDARD UPDATE (Modify)
  // ---------------------------------------------------------------------------

  const updatedSelection: SelectionRange = {
    ...activeSelection,
    lead: event.cell
  };

  // We must preserve the other selections if we had multiple
  const nextSelections = [...state.selections];

  nextSelections[activeSelectionIndex] = updatedSelection;

  if (event.isDragging) {
    return {
      delta: {
        kind: 'selectionPreview',
        source: event.source,
        activeRange: updatedSelection
      },
      next: {
        ...state,
        selections: nextSelections,
        lastInteractionSource: event.source
      }
    };
  }

  return {
    delta: {
      kind: 'selectionModified',
      source: event.source,
      updated: [updatedSelection]
      // We do NOT send focusChange here.
      // This tells the renderer "Keep the blue box exactly where it is."
    },
    next: {
      ...state,
      // We preserve the existing focus
      // focusedCell: state.focusedCell (implied by spread)
      selections: nextSelections,
      lastInteractionSource: event.source
    }
  };
}
